import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, Download, HelpCircle, Plus, Trash2, AlertCircle } from 'lucide-react';

interface Investment {
  id: string;
  symbol: string;
  name: string;
  purchaseDate: string;
  purchasePrice: number;
  currentPrice: number;
  shares: number;
  fees: number;
}

interface HarvestingOpportunity {
  investment: Investment;
  unrealizedLoss: number;
  taxSavings: number;
  daysUntilWashSale: number;
}

export default function TaxLossHarvestingCalculator() {
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [taxRate, setTaxRate] = useState<string>('20');
  const [newInvestment, setNewInvestment] = useState<Omit<Investment, 'id'>>({
    symbol: '',
    name: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    purchasePrice: 0,
    currentPrice: 0,
    shares: 0,
    fees: 0
  });

  const addInvestment = () => {
    if (!newInvestment.symbol || !newInvestment.name || newInvestment.shares <= 0) {
      return;
    }

    const investment: Investment = {
      ...newInvestment,
      id: `inv-${Date.now()}`
    };

    setInvestments([...investments, investment]);
    setNewInvestment({
      symbol: '',
      name: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      purchasePrice: 0,
      currentPrice: 0,
      shares: 0,
      fees: 0
    });
    setShowAddForm(false);
  };

  const removeInvestment = (id: string) => {
    setInvestments(investments.filter(inv => inv.id !== id));
  };

  const calculateUnrealizedLoss = (investment: Investment): number => {
    const totalCost = (investment.purchasePrice * investment.shares) + investment.fees;
    const currentValue = investment.currentPrice * investment.shares;
    return currentValue - totalCost;
  };

  const calculateTaxSavings = (loss: number): number => {
    return Math.abs(loss) * (Number(taxRate) / 100);
  };

  const calculateDaysUntilWashSale = (purchaseDate: string): number => {
    const purchase = new Date(purchaseDate);
    const today = new Date();
    const thirtyDaysFromPurchase = new Date(purchase);
    thirtyDaysFromPurchase.setDate(thirtyDaysFromPurchase.getDate() + 30);
    
    if (today > thirtyDaysFromPurchase) return 0;
    
    const diffTime = thirtyDaysFromPurchase.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const findHarvestingOpportunities = (): HarvestingOpportunity[] => {
    return investments
      .map(investment => {
        const unrealizedLoss = calculateUnrealizedLoss(investment);
        // Only include investments with losses
        if (unrealizedLoss >= 0) return null;
        
        return {
          investment,
          unrealizedLoss,
          taxSavings: calculateTaxSavings(unrealizedLoss),
          daysUntilWashSale: calculateDaysUntilWashSale(investment.purchaseDate)
        };
      })
      .filter((opportunity): opportunity is HarvestingOpportunity => opportunity !== null)
      .sort((a, b) => Math.abs(b.unrealizedLoss) - Math.abs(a.unrealizedLoss));
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadReport = () => {
    const opportunities = findHarvestingOpportunities();
    let csv = 'Tax Loss Harvesting Opportunities\n\n';
    
    // Summary
    csv += `Tax Rate: ${taxRate}%\n\n`;
    
    // All Investments
    csv += 'Current Investments\n';
    csv += 'Symbol,Name,Purchase Date,Purchase Price,Current Price,Shares,Fees,Unrealized Gain/Loss\n';
    investments.forEach(inv => {
      const unrealizedGainLoss = calculateUnrealizedLoss(inv);
      csv += `${inv.symbol},${inv.name},${inv.purchaseDate},${inv.purchasePrice},${inv.currentPrice},${inv.shares},${inv.fees},${unrealizedGainLoss}\n`;
    });
    
    // Harvesting Opportunities
    csv += '\nHarvesting Opportunities\n';
    csv += 'Symbol,Unrealized Loss,Potential Tax Savings,Days Until Wash Sale Clear\n';
    opportunities.forEach(opp => {
      csv += `${opp.investment.symbol},${opp.unrealizedLoss},${opp.taxSavings},${opp.daysUntilWashSale}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tax-loss-harvesting-report.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const opportunities = findHarvestingOpportunities();
  const totalPotentialSavings = opportunities.reduce((sum, opp) => sum + opp.taxSavings, 0);

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/tools"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Tax-Loss Harvesting Calculator
              </h1>
              <p className="text-gray-600">
                Identify tax-loss harvesting opportunities in your portfolio
              </p>
            </div>
            <div className="w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-brand-700" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Investment List Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Investment Portfolio</h2>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowInfo(!showInfo)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <HelpCircle className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="inline-flex items-center px-4 py-2 bg-brand-900 text-white rounded-lg hover:bg-brand-800 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Investment
                  </button>
                </div>
              </div>

              {showInfo && (
                <div className="mb-6 p-4 bg-brand-50 rounded-lg">
                  <h3 className="font-medium text-brand-900 mb-2">About Tax-Loss Harvesting</h3>
                  <p className="text-sm text-brand-800">
                    Tax-loss harvesting is the practice of selling investments at a loss to offset capital gains tax liability. This calculator helps identify opportunities for tax-loss harvesting while considering the wash-sale rule.
                  </p>
                </div>
              )}

              {/* Tax Rate Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Capital Gains Tax Rate
                </label>
                <div className="relative w-full md:w-1/3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="w-full pr-8 py-2 px-4 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
              </div>

              {showAddForm && (
                <div className="mb-6 p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Add Investment</h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Symbol
                      </label>
                      <input
                        type="text"
                        value={newInvestment.symbol}
                        onChange={(e) => setNewInvestment({ ...newInvestment, symbol: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Investment Name
                      </label>
                      <input
                        type="text"
                        value={newInvestment.name}
                        onChange={(e) => setNewInvestment({ ...newInvestment, name: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Purchase Date
                      </label>
                      <input
                        type="date"
                        value={newInvestment.purchaseDate}
                        onChange={(e) => setNewInvestment({ ...newInvestment, purchaseDate: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of Shares
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.0001"
                        value={newInvestment.shares || ''}
                        onChange={(e) => setNewInvestment({ ...newInvestment, shares: Number(e.target.value) })}
                        className="w-full px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Purchase Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newInvestment.purchasePrice || ''}
                          onChange={(e) => setNewInvestment({ ...newInvestment, purchasePrice: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newInvestment.currentPrice || ''}
                          onChange={(e) => setNewInvestment({ ...newInvestment, currentPrice: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Trading Fees
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newInvestment.fees || ''}
                          onChange={(e) => setNewInvestment({ ...newInvestment, fees: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addInvestment}
                      className="px-4 py-2 bg-brand-900 text-white rounded-lg hover:bg-brand-800 transition-colors"
                    >
                      Add Investment
                    </button>
                  </div>
                </div>
              )}

              {/* Investments List */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                      <th className="pb-3 font-medium">Symbol</th>
                      <th className="pb-3 font-medium">Purchase Date</th>
                      <th className="pb-3 font-medium">Shares</th>
                      <th className="pb-3 font-medium">Purchase Price</th>
                      <th className="pb-3 font-medium">Current Price</th>
                      <th className="pb-3 font-medium">Gain/Loss</th>
                      <th className="pb-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {investments.map((investment) => {
                      const unrealizedGainLoss = calculateUnrealizedLoss(investment);
                      return (
                        <tr key={investment.id} className="border-b border-gray-100">
                          <td className="py-3">
                            <div className="font-medium text-gray-900">{investment.symbol}</div>
                            <div className="text-xs text-gray-500">{investment.name}</div>
                          </td>
                          <td className="py-3">{new Date(investment.purchaseDate).toLocaleDateString()}</td>
                          <td className="py-3">{investment.shares.toFixed(4)}</td>
                          <td className="py-3">{formatCurrency(investment.purchasePrice)}</td>
                          <td className="py-3">{formatCurrency(investment.currentPrice)}</td>
                          <td className={`py-3 ${unrealizedGainLoss >= 0 ? 'text-brand-700' : 'text-red-600'}`}>
                            {formatCurrency(unrealizedGainLoss)}
                          </td>
                          <td className="py-3">
                            <button
                              onClick={() => removeInvestment(investment.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {investments.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No investments added yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Harvesting Opportunities</h2>
                <button
                  onClick={downloadReport}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                  disabled={investments.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Report
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Potential Tax Savings</p>
                  <p className="text-2xl font-bold text-brand-700">
                    {formatCurrency(totalPotentialSavings)}
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900">Recommended Actions</h3>
                  {opportunities.length > 0 ? (
                    opportunities.map((opportunity, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-medium text-gray-900">{opportunity.investment.symbol}</h4>
                            <p className="text-sm text-gray-500">{opportunity.investment.name}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-red-600 font-medium">
                              {formatCurrency(opportunity.unrealizedLoss)}
                            </div>
                            <div className="text-sm text-brand-700">
                              Save up to {formatCurrency(opportunity.taxSavings)}
                            </div>
                          </div>
                        </div>
                        {opportunity.daysUntilWashSale > 0 && (
                          <div className="text-sm text-gray-500">
                            Wait {opportunity.daysUntilWashSale} days to avoid wash sale
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">
                      No tax-loss harvesting opportunities found
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-brand-50 rounded-xl p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-brand-700 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-brand-900 mb-2">Important Notes</h3>
                  <ul className="text-sm text-brand-800 space-y-2">
                    <li>The wash-sale rule prohibits claiming a loss on a security if you buy the same or substantially identical security within 30 days</li>
                    <li>Consider transaction costs when evaluating harvesting opportunities</li>
                    <li>Tax-loss harvesting should be part of a broader investment strategy</li>
                    <li>Consult with a tax professional for specific advice</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}